import { api, ApiError } from "../api";
import { platformLabel } from "../platforms";
import { emptyBlock, errorBanner, loadingBlock, pill, tableRegion } from "./shared/blocks";
import { h, safeUrl, tidy } from "./shared/safe";

type StatusFilter = "all" | "available" | "unavailable" | "unverifiable" | "unchecked";
type VerificationRow = {
  id: string;
  client_id?: string | null;
  platform?: string;
  url?: string;
  http_status: number | null;
  control_404_pass: boolean | null;
  link_to_money_found: boolean | null;
  rel_attribute?: string | null;
  entity_found: boolean | null;
  indexed?: boolean | null;
  checked_at?: number | null;
};
const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All pages" }, { id: "available", label: "Page available" },
  { id: "unavailable", label: "Page unavailable" }, { id: "unverifiable", label: "Unverifiable" },
  { id: "unchecked", label: "Not checked" },
];
let viewState: { filter: StatusFilter; search: string } = { filter: "all", search: "" };

export async function verificationGlobalView(root: HTMLElement): Promise<void> {
  const shell = h("section", { class: "cp-checks" });
  root.replaceChildren(shell);
  shell.append(
    h("div", { class: "topbar" }, h("div", null, h("h1", null, "Delivery checks"), h("p", { class: "muted" }, "Review page availability and expected link evidence for CloudPush publications."))),
    h("div", { class: "cp-page-note cp-checks__intro", role: "note" },
      h("strong", null, "These checks cover CloudPush deployments only."),
      " Page availability and expected link presence are separate results: a reachable page does not confirm that the expected client link is present.",
    ),
  );
  const content = h("div", { class: "cp-checks__content" });
  content.append(loadingBlock("Loading delivery checks", 5));
  shell.append(content);

  try {
    let verifications: VerificationRow[] = [];
    let clients = new Map<string, string>();
    const [checksReply, clientsReply] = await Promise.all([
      api<{ verifications: VerificationRow[] }>("/v1/verification"),
      api<{ clients: { id?: string; business_name?: string }[] }>("/v1/clients?limit=200&offset=0").catch(() => null),
    ]);
    verifications = Array.isArray(checksReply.verifications) ? checksReply.verifications : [];
    if (clientsReply && Array.isArray(clientsReply.clients)) clients = new Map(clientsReply.clients.filter((c) => typeof c.id === "string").map((c) => [c.id!, tidy(c.business_name, 300)]));
    if (!verifications.length) {
      content.replaceChildren(emptyBlock("No CloudPush deployments to check yet", "When you deploy a client website, delivery checks for those pages will appear here."));
      return;
    }

    const unverifiable = (v: VerificationRow) => v.http_status === 200 && v.control_404_pass === false;
    const counts = () => ({
      all: verifications.length,
      available: verifications.filter((v) => v.http_status === 200 && !unverifiable(v)).length,
      unavailable: verifications.filter((v) => v.http_status !== null && v.http_status !== 200).length,
      unverifiable: verifications.filter(unverifiable).length,
      unchecked: verifications.filter((v) => v.http_status === null).length,
    });
    const stats = h("div", { class: "cp-checks__stats", role: "group", "aria-label": "Filter delivery checks by page availability" });
    const statButtons = new Map<StatusFilter, HTMLButtonElement>();
    for (const filter of FILTERS) {
      const button = h("button", { type: "button", class: "cp-check-stat", dataset: { filter: filter.id }, "aria-pressed": "false" },
        h("span", { class: "cp-check-stat__count" }, "0"), h("span", { class: "cp-check-stat__label" }, filter.label),
      ) as HTMLButtonElement;
      button.addEventListener("click", () => { viewState.filter = filter.id; paint(); });
      statButtons.set(filter.id, button);
      stats.append(button);
    }
    const countNote = h("p", { class: "muted cp-checks__count-note" }, "Each total represents CloudPush deployment records. It is separate from the Links Created count.");
    const mobileNote = h("p", { class: "cp-checks__mobile-note" }, "On a small screen, scroll the table sideways to see every status and action.");
    const search = h("input", { type: "search", "aria-label": "Search delivery checks", placeholder: "Search client, platform, or page URL", value: viewState.search, autocomplete: "off" }) as HTMLInputElement;
    const toolbar = h("div", { class: "cp-checks__toolbar", role: "search" }, h("label", null, h("span", null, "Search delivery checks"), search));
    const notice = h("p", { class: "cp-checks__notice", role: "status", "aria-live": "polite", hidden: true });
    const results = h("div", { class: "cp-checks__results" });
    content.replaceChildren(stats, countNote, mobileNote, toolbar, notice, results);
    search.addEventListener("input", () => { viewState.search = search.value; paintRows(); });

    function clientName(v: VerificationRow): string { return (v.client_id && clients.get(v.client_id)) || "Client record unavailable"; }
    function availability(v: VerificationRow): HTMLElement {
      if (v.http_status === null) return pill("Not checked", "neutral");
      if (unverifiable(v)) return pill("Unverifiable · 404 control failed", "warn");
      return v.http_status === 200 ? pill("Page available · 200", "ok") : pill(`Page unavailable · ${v.http_status}`, "bad");
    }
    function evidence(v: VerificationRow): HTMLElement {
      if (v.link_to_money_found == null) return pill("Not confirmed", "neutral");
      return v.link_to_money_found ? pill("Expected link found", "ok") : pill("Expected link not found", "warn");
    }
    function binary(value: boolean | null | undefined, yes: string, no: string): HTMLElement {
      if (value === null || value === undefined) return pill("Not checked", "neutral");
      return value ? pill(yes, "ok") : pill(no, "warn");
    }
    function moreChecks(v: VerificationRow): HTMLElement {
      const rel = typeof v.rel_attribute === "string" && v.rel_attribute.trim() ? v.rel_attribute : "No link attribute recorded";
      return h("details", { class: "cp-checks__more" }, h("summary", null, "Other checks"),
        h("dl", null,
          h("dt", null, "Missing-page handling"), h("dd", null, binary(v.control_404_pass, "Returns not found", "Did not return not found")),
          h("dt", null, "Search indexing"), h("dd", null, binary(v.indexed ?? null, "Reported indexed", "Not reported indexed")),
          h("dt", null, "Link attribute"), h("dd", null, tidy(rel, 120)),
          h("dt", null, "Business name"), h("dd", null, binary(v.entity_found, "Found on page", "Not found on page")),
        ),
      );
    }
    function paint() {
      const current = counts();
      for (const filter of FILTERS) {
        const button = statButtons.get(filter.id)!;
        button.querySelector(".cp-check-stat__count")!.textContent = String(current[filter.id]);
        const selected = viewState.filter === filter.id;
        button.setAttribute("aria-pressed", String(selected));
      }
      paintRows();
    }
    function paintRows() {
      const term = viewState.search.trim().toLowerCase();
      const rows = verifications.filter((v) => {
        const statusMatch = viewState.filter === "all" ||
          (viewState.filter === "available" && v.http_status === 200 && !unverifiable(v)) ||
          (viewState.filter === "unavailable" && v.http_status !== null && v.http_status !== 200) ||
          (viewState.filter === "unverifiable" && unverifiable(v)) ||
          (viewState.filter === "unchecked" && v.http_status === null);
        return statusMatch && (!term || `${clientName(v)} ${platformLabel(v.platform ?? "")} ${v.url ?? ""}`.toLowerCase().includes(term));
      });
      if (!rows.length) return void results.replaceChildren(emptyBlock("No delivery checks match", "Change the search or choose All pages."));
      const table = h("table", { class: "qf-table cp-checks__table" },
        h("caption", { class: "qf-sr-only" }, `CloudPush delivery checks, ${rows.length} rows.`),
        h("thead", null, h("tr", null,
          h("th", { scope: "col" }, "Client"), h("th", { scope: "col" }, "Published page"), h("th", { scope: "col" }, "Page availability"),
          h("th", { scope: "col" }, "Expected client link"), h("th", { scope: "col" }, "Other checks"), h("th", { scope: "col" }, "Last checked"), h("th", { scope: "col" }, "Action"),
        )),
        h("tbody", null, ...rows.map((v) => {
          const url = typeof v.url === "string" ? v.url : "";
          let href: string | null = null;
          try { const parsed = new URL(url); if (parsed.protocol === "http:" || parsed.protocol === "https:") href = safeUrl(url); } catch { /* Show malformed addresses as text. */ }
          const page = href ? h("a", { href, target: "_blank", rel: "noopener noreferrer", class: "qf-wrap", title: url }, url) : h("span", { class: "qf-wrap" }, url || "URL unavailable");
          const recheck = h("button", { type: "button", class: "secondary cp-checks__recheck", dataset: { id: v.id } }, "Re-check page") as HTMLButtonElement;
          const row = h("tr", { dataset: { deployment: v.id } },
            h("td", null, h("strong", { class: "qf-wrap" }, clientName(v)), h("div", { class: "muted" }, platformLabel(v.platform ?? "unknown"))),
            h("td", null, page), h("td", null, availability(v)), h("td", null, evidence(v)), h("td", null, moreChecks(v)),
            h("td", null, v.checked_at ? new Date(v.checked_at * 1000).toLocaleString() : "Not checked"), h("td", null, recheck),
          );
          recheck.addEventListener("click", async () => {
            recheck.disabled = true;
            recheck.textContent = "Checking…";
            notice.hidden = false;
            notice.textContent = `Checking ${clientName(v)}. This does not count or change created-link records.`;
            try {
              await api(`/v1/verification/run/${encodeURIComponent(v.id)}`, { method: "POST" });
              const latest = await api<{ verifications: VerificationRow[] }>("/v1/verification");
              if (Array.isArray(latest.verifications)) verifications = latest.verifications;
              notice.textContent = `Check refreshed for ${clientName(v)}. Page availability and link evidence remain separate results.`;
              paint();
            } catch (error) {
              recheck.disabled = false;
              recheck.textContent = "Re-check page";
              notice.classList.add("is-error");
              notice.textContent = error instanceof ApiError ? `The check could not be completed (HTTP ${error.status}).` : "The check could not be completed. Your filters are still here.";
              notice.setAttribute("role", "alert");
            }
          });
          return row;
        })),
      );
      results.replaceChildren(tableRegion("CloudPush delivery checks", table));
    }
    paint();
  } catch (error) {
    const message = error instanceof ApiError ? `Delivery checks could not be loaded (HTTP ${error.status}).` : "Delivery checks could not be loaded. Try again.";
    content.replaceChildren(errorBanner(message, { retry: () => void verificationGlobalView(root) }));
  }
}
